import { signOut, useSession } from "@/lib/auth-client";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	BarChart3,
	CalendarClock,
	ChevronLeft,
	ChevronRight,
	ClipboardCheck,
	FileText,
	LayoutDashboard,
	LogOut,
	type LucideIcon,
	Receipt,
	Settings,
	TriangleAlert,
	Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

type NavItem = { label: string; href: string; icon: LucideIcon; badge?: "drafts" | "attention" };
type NavSection = { label: "OPERATE" | "RECORDS" | "SYSTEM"; items: NavItem[] };

// The Help affordance lives at the route level as a floating action button
// (`@/features/help/FloatingHelpButton`), NOT in the sidebar. It moved out of
// SYSTEM in the Phase 7 follow-up after `df7717b` so the trigger floats over
// every authenticated page regardless of sidebar collapse state. The SYSTEM
// section is back to Settings only.

const navSections: NavSection[] = [
	{
		label: "OPERATE",
		items: [
			{ label: "Overview", href: "/", icon: LayoutDashboard },
			{ label: "Billing Cycle", href: "/cycle", icon: CalendarClock },
			{
				label: "Attention",
				href: "/attention",
				icon: TriangleAlert,
				badge: "attention",
			},
			{
				label: "Review Queue",
				href: "/invoices/review",
				icon: ClipboardCheck,
				badge: "drafts",
			},
		],
	},
	{
		label: "RECORDS",
		items: [
			{ label: "Documents", href: "/documents", icon: FileText },
			{ label: "Customers", href: "/customers", icon: Users },
			{ label: "Invoices", href: "/invoices", icon: Receipt },
			{ label: "Reconciliation", href: "/reports", icon: BarChart3 },
		],
	},
	{
		label: "SYSTEM",
		items: [{ label: "Settings", href: "/settings/profile", icon: Settings }],
	},
];

export function AppSidebar() {
	const [collapsed, setCollapsed] = useState(false);
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;
	const { data: session } = useSession();
	// Skip data queries until the user is both 2FA-enrolled and onboarded.
	// The API blocks data endpoints with 2FA_REQUIRED otherwise, and we'd
	// rather the sidebar render a clean empty state than a toast storm while
	// the user is still in the onboarding wizard.
	const canLoadAppData =
		(session?.user as { twoFactorEnabled?: boolean } | undefined)?.twoFactorEnabled === true &&
		Boolean((session?.user as { onboardedAt?: Date | string | null } | undefined)?.onboardedAt);
	const { data: summary } = trpc.invoices.dashboardSummary.useQuery(
		{},
		{ enabled: canLoadAppData },
	);
	const { data: attentionCounts } = trpc.extractions.attentionCounts.useQuery(undefined, {
		enabled: canLoadAppData,
	});
	const draftCount = summary?.draftsToReview ?? 0;
	const attentionCount = attentionCounts
		? Object.values(attentionCounts).reduce((sum, n) => sum + n, 0)
		: 0;

	const isItemActive = (href: string) => {
		if (href === "/") return currentPath === "/";
		if (href === "/invoices/review") return currentPath === "/invoices/review";
		if (href === "/invoices")
			return currentPath.startsWith("/invoices") && currentPath !== "/invoices/review";
		// Settings has a default child (/settings/profile) but should light up
		// for any settings subpage — Workspace tabs included.
		if (href === "/settings/profile") return currentPath.startsWith("/settings");
		return currentPath.startsWith(href);
	};

	return (
		<aside
			className={cn(
				"flex flex-col border-r border-[#e5e7eb] bg-white transition-[width] duration-200",
				collapsed ? "w-16" : "w-56",
			)}
		>
			{/* Logo + collapse toggle */}
			<div className="flex h-14 items-center justify-between px-4">
				{!collapsed && (
					<span className="text-[15px] font-bold tracking-tight text-[#1e1e20]">
						Payroll<span className="text-[#4f46e5]">Mart</span>
					</span>
				)}
				<button
					type="button"
					onClick={() => setCollapsed(!collapsed)}
					className={cn(
						"flex h-7 w-7 items-center justify-center rounded-md text-[#9ca3af] transition-colors hover:bg-[#f3f4f6] hover:text-[#1e1e20]",
						collapsed && "mx-auto",
					)}
					aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
				>
					{collapsed ? (
						<ChevronRight className="h-4 w-4" strokeWidth={2} />
					) : (
						<ChevronLeft className="h-4 w-4" strokeWidth={2} />
					)}
				</button>
			</div>

			{/* Navigation */}
			<nav className="flex flex-1 flex-col px-3 pt-2 pb-3">
				{navSections.map((section, sectionIdx) => (
					<div key={section.label} className={cn(section.label === "SYSTEM" && "mt-auto")}>
						{!collapsed ? (
							<div
								className={cn(
									"px-3 pb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[#9ca3af]",
									sectionIdx === 0 ? "pt-1" : "pt-4",
								)}
							>
								{section.label}
							</div>
						) : (
							sectionIdx > 0 && <div className="mx-3 my-2 border-t border-[#f3f4f6]" />
						)}
						<div className="space-y-0.5">
							{section.items.map((item) => {
								const isActive = isItemActive(item.href);
								return (
									<Link
										key={item.href}
										to={item.href}
										title={collapsed ? item.label : undefined}
										className={cn(
											"relative flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-colors duration-150",
											isActive
												? "bg-[#eef2ff] text-[#4f46e5]"
												: "text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#1e1e20]",
											collapsed && "justify-center px-0",
										)}
									>
										{isActive && (
											<span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#4f46e5]" />
										)}
										<item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.5} />
										{!collapsed && item.label}
										{!collapsed && item.badge === "drafts" && draftCount > 0 && (
											<span className="ml-auto rounded-full bg-[#4f46e5] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
												{draftCount}
											</span>
										)}
										{collapsed && item.badge === "drafts" && draftCount > 0 && (
											<span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#4f46e5] text-[9px] font-semibold text-white">
												{draftCount}
											</span>
										)}
										{!collapsed && item.badge === "attention" && attentionCount > 0 && (
											<span className="ml-auto rounded-full bg-[#d97706] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
												{attentionCount}
											</span>
										)}
										{collapsed && item.badge === "attention" && attentionCount > 0 && (
											<span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#d97706] text-[9px] font-semibold text-white">
												{attentionCount}
											</span>
										)}
									</Link>
								);
							})}
						</div>
					</div>
				))}
			</nav>

			{/* Bottom: Profile */}
			<div className="border-t border-[#e5e7eb] px-3 py-3">
				<ProfileButton collapsed={collapsed} />
			</div>
		</aside>
	);
}

function ProfileButton({ collapsed }: { collapsed: boolean }) {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const { data: sessionData } = useSession();
	const { data: avatarUrl } = trpc.profile.avatarUrl.useQuery();

	const user = sessionData?.user;
	const name = user?.name ?? "";
	const parts = name.split(/\s+/).filter(Boolean);
	const first = parts.at(0) ?? "";
	const last = parts.at(-1) ?? "";
	const initials =
		parts.length >= 2
			? (first.charAt(0) + last.charAt(0)).toUpperCase()
			: first
				? first.charAt(0).toUpperCase()
				: "?";
	const displayName =
		parts.length >= 2
			? `${first} ${last.charAt(0)}.`
			: first || user?.email?.split("@")[0] || "User";

	// Close on outside click
	useEffect(() => {
		if (!open) return;
		function handleClick(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [open]);

	return (
		<div ref={ref} className="relative">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className={cn(
					"flex w-full items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-colors duration-150 text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#1e1e20]",
					collapsed && "justify-center px-0",
				)}
			>
				{avatarUrl ? (
					<img src={avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" />
				) : (
					<div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#4f46e5] text-[10px] font-semibold text-white shrink-0">
						{initials}
					</div>
				)}
				{!collapsed && <span className="truncate text-left text-[#1e1e20]">{displayName}</span>}
			</button>

			{/* Popup menu */}
			{open && (
				<div
					className={cn(
						"absolute bottom-full mb-1 w-56 rounded-lg border border-[#e5e7eb] bg-white py-1 shadow-lg",
						collapsed ? "left-full ml-2 bottom-0 mb-0" : "left-0",
					)}
				>
					<div className="px-3 py-2">
						<p className="truncate text-[13px] font-medium text-[#1e1e20]">{user?.name ?? "—"}</p>
						<p className="truncate text-xs text-[#6b7280]">{user?.email ?? "—"}</p>
					</div>
					<div className="my-1 border-t border-[#f3f4f6]" />
					<button
						type="button"
						onClick={async () => {
							setOpen(false);
							try {
								await signOut();
								window.location.href = "/login";
							} catch (error) {
								toast.error(error instanceof Error ? error.message : "Failed to sign out");
							}
						}}
						className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-[#dc2626] hover:bg-[#fef2f2]"
					>
						<LogOut className="h-4 w-4" strokeWidth={1.5} />
						Sign Out
					</button>
				</div>
			)}
		</div>
	);
}
